import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { FiChevronDown, FiChevronRight, FiChevronLeft, FiGrid, FiMenu } from 'react-icons/fi';
import { MenuItemConfig } from '../types/actionCodes';
import AppBrand from '@core/components/AppBrand';
import './EntitySidebar.css';

interface EntitySidebarProps {
  menuItems: MenuItemConfig[];
  loading?: boolean;
  entityInfo: {
    scope: string;
    scopeId: number;
    scopeName: string;
  };
  /** Call when a nav link is clicked (e.g. to close mobile sidebar) */
  onCloseMobileMenu?: () => void;
}

const EXPANDED_WIDTH = 260;
const COLLAPSED_WIDTH = 40;

function getScopeBadgeClass(scope: string): string {
  switch (scope) {
    case 'NATION': return 'bg-danger';
    case 'DISTRICT': return 'bg-info';
    case 'CHURCH': return 'bg-success';
    default: return 'bg-secondary';
  }
}

const EntitySidebar: React.FC<EntitySidebarProps> = ({
  menuItems,
  loading = false,
  entityInfo,
  onCloseMobileMenu,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('ntcog-sidebar-collapsed') === 'true';
  });

  useEffect(() => {
    const syncCollapsedPreference = () => {
      setCollapsed(localStorage.getItem('ntcog-sidebar-collapsed') === 'true');
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'ntcog-sidebar-collapsed') {
        syncCollapsedPreference();
      }
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('ntcog-sidebar-preference-updated', syncCollapsedPreference);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('ntcog-sidebar-preference-updated', syncCollapsedPreference);
    };
  }, []);

  const toggleCollapse = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('ntcog-sidebar-collapsed', String(next));
      return next;
    });
  };

  const toggleExpand = (itemId: string) => {
    setExpandedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleNavigation = (path?: string) => {
    onCloseMobileMenu?.();
    if (path) navigate(path);
  };

  const handleSwitchEntity = () => {
    onCloseMobileMenu?.();
    navigate('/entities');
  };

  const isItemActive = (item: MenuItemConfig): boolean => {
    if (item.path && location.pathname === item.path) return true;
    if (item.children) {
      return item.children.some(child => child.path && location.pathname === child.path);
    }
    return false;
  };

  const renderMenuItem = (item: MenuItemConfig, depth: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const isActive = item.path === location.pathname;
    const isSectionActive = isItemActive(item);

    if (collapsed && depth > 0) return null;

    const navItemClass = [
      'd-flex align-items-center',
      collapsed ? 'justify-content-center' : 'justify-content-between',
      'entity-sidebar__nav-item',
      (isActive || (isSectionActive && depth === 0)) ? 'entity-sidebar__nav-item--active' : ''
    ].filter(Boolean).join(' ');

    const style: React.CSSProperties = {
      padding: collapsed ? '10px 0' : (depth === 0 ? '10px 16px' : '8px 16px'),
      paddingLeft: collapsed ? '0' : (depth === 0 ? '16px' : `${16 + depth * 20}px`),
      fontSize: 'var(--ntcog-text-sm)',
      fontWeight: depth === 0 ? 600 : 400,
      letterSpacing: depth === 0 ? '0.5px' : '0',
      textTransform: depth === 0 ? 'uppercase' : 'none',
      minHeight: 'var(--ntcog-touch-target)',
    };

    if (hasChildren && !item.path) {
      return (
        <div key={item.id}>
          <button
            type="button"
            className={navItemClass}
            style={style}
            title={collapsed ? item.label : undefined}
            aria-expanded={isExpanded}
            aria-haspopup="true"
            aria-current={isSectionActive ? 'true' : undefined}
            onClick={() => {
              if (collapsed) {
                setCollapsed(false);
                localStorage.setItem('ntcog-sidebar-collapsed', 'false');
                setExpandedItems(prev => [...prev, item.id]);
              } else {
                toggleExpand(item.id);
              }
            }}
          >
            {collapsed ? (
              <span style={{ fontSize: '15px', opacity: 0.9 }} aria-hidden>{item.icon}</span>
            ) : (
              <>
                <div className="d-flex align-items-center gap-2">
                  {item.icon && <span className="pb-1" style={{ fontSize: '16px', opacity: 0.9 }} aria-hidden>{item.icon}</span>}
                  <span>{item.label}</span>
                </div>
                <span style={{ fontSize: '12px', opacity: 0.6 }} aria-hidden>
                  {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                </span>
              </>
            )}
          </button>
          {!collapsed && hasChildren && isExpanded && (
            <div>
              {item.children!.map(child => renderMenuItem(child, depth + 1))}
            </div>
          )}
        </div>
      );
    }

    if (item.path) {
      if (collapsed && hasChildren) {
        return (
          <div key={item.id}>
            <button
              type="button"
              className={navItemClass}
              style={style}
              title={item.label}
              onClick={() => {
                setCollapsed(false);
                localStorage.setItem('ntcog-sidebar-collapsed', 'false');
                toggleExpand(item.id);
                setTimeout(() => handleNavigation(item.path), 0);
              }}
            >
              <span style={{ fontSize: '15px', opacity: 0.9 }} aria-hidden>{item.icon}</span>
            </button>
          </div>
        );
      }
      return (
        <div key={item.id}>
          <NavLink
            to={item.path!}
            className={navItemClass}
            style={style}
            title={collapsed ? item.label : undefined}
            aria-current={location.pathname === item.path ? 'page' : undefined}
            onClick={() => onCloseMobileMenu?.()}
          >
            {collapsed ? (
              <span style={{ fontSize: '15px', opacity: 0.9 }} aria-hidden>{item.icon}</span>
            ) : (
              <>
                <div className="d-flex align-items-center gap-2">
                  {item.icon && <span className="pb-1" style={{ fontSize: '16px', opacity: 0.9 }} aria-hidden>{item.icon}</span>}
                  <span>{item.label}</span>
                </div>
                {hasChildren && (
                  <span style={{ fontSize: '12px', opacity: 0.6 }} aria-hidden>
                    {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                  </span>
                )}
              </>
            )}
          </NavLink>
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className="entity-sidebar d-flex flex-column"
      style={{
        width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
      }}
    >
      <div
        className="entity-sidebar__header"
        style={{ padding: collapsed ? '14px 0' : '20px 16px 12px' }}
      >
        {collapsed ? (
          <button
            type="button"
            className="entity-sidebar__collapse-btn"
            style={{ width: '100%', textAlign: 'center' }}
            onClick={toggleCollapse}
            title="Expand sidebar"
            aria-label="Expand sidebar"
          >
            <FiMenu size={16} aria-hidden />
          </button>
        ) : (
          <>
            <AppBrand
              compact
              titleClassName="entity-sidebar__brand-title"
              subtitleClassName="entity-sidebar__brand-subtitle"
            />
            <button
              type="button"
              className="entity-sidebar__collapse-btn"
              onClick={toggleCollapse}
              title="Collapse sidebar"
              aria-label="Collapse sidebar"
            >
              <FiChevronLeft size={16} aria-hidden />
            </button>
          </>
        )}
      </div>

      {!collapsed && (
        <div className="entity-sidebar__section">
          <button
            type="button"
            className="d-flex align-items-center justify-content-between w-100 border-0 bg-transparent text-start entity-sidebar__switch-entity"
            onClick={handleSwitchEntity}
            title="Back to accounts"
            aria-label="Switch account or back to account list"
          >
            <div className="w-100 text-center me-2" style={{ minWidth: 0 }}>
            {/* <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}> */}
              <p
                className={`entity-sidebar__scope-badge ${getScopeBadgeClass(entityInfo.scope)} m-0`}
              >
                {entityInfo.scope}
              </p>
              {/* <small
                style={{
                  color: 'var(--ntcog-sidebar-text)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 'var(--ntcog-text-xs)',
                }}
              >
                {entityInfo.scopeName}
              </small> */}
            </div>
            <span className="entity-sidebar__collapse-btn mb-1" style={{ flexShrink: 0 }} aria-hidden>
              <FiGrid size={20} />
            </span>
          </button>
        </div>
      )}

      {collapsed && (
        <div className="entity-sidebar__section" style={{ textAlign: 'center', padding: '8px 0' }}>
          <button
            type="button"
            className="border-0 bg-transparent p-0"
            onClick={handleSwitchEntity}
            title="Back to accounts"
            aria-label="Switch account or back to account list"
          >
            <span
              className={`entity-sidebar__scope-badge entity-sidebar__scope-badge--collapsed ${getScopeBadgeClass(entityInfo.scope)}`}
            >
              {entityInfo.scope.charAt(0)}
            </span>
          </button>
        </div>
      )}

      <div className="entity-sidebar__menu">
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" variant="light" aria-label="Loading menu" />
            {!collapsed && (
              <p className="entity-sidebar__copyright" style={{ marginTop: '8px' }}>
                Loading menu...
              </p>
            )}
          </div>
        ) : (
          <nav aria-label="Main navigation" className="entity-sidebar__nav">
            {menuItems.map(item => renderMenuItem(item))}
          </nav>
        )}
      </div>

    </div>
  );
};

export default EntitySidebar;
